import { useEffect, useState } from "react";
import { Users, FileText, Map, Clock, Banknote, ArrowRight, KeyRound, Mail, Loader2 } from "lucide-react";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { toast } from "../../../components/ui/sonner";

const CARDS = [
  { key: "total_users", label: "Total Registered Users", icon: Users, border: "border-[#00B4D8]/40" },
  { key: "pending_kycs", label: "Pending KYCs", icon: FileText, border: "border-amber-300/50" },
  { key: "distance_approved_km", label: "Distance Approved (km)", icon: Map, border: "border-[#4CAF50]/50" },
  { key: "pending_mappings", label: "Pending Mappings", icon: Clock, border: "border-[#00B4D8]/40" },
  { key: "pending_withdrawals", label: "Pending Withdrawals", icon: Banknote, border: "border-red-400/50" },
];

export default function OverviewTab({ onNavigate }) {
  const { authFetch } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestLoading, setDigestLoading] = useState(false);

  const sendDigest = async (dryRun) => {
    if (!dryRun && !window.confirm("Send the weekly digest email to every user who earned this week?")) return;
    setDigestLoading(true);
    try {
      const params = new URLSearchParams({ only_with_earnings: "true", dry_run: dryRun ? "true" : "false" });
      const data = await authFetch(`/api/admin/digest/send-weekly?${params.toString()}`, { method: "POST" });
      toast(dryRun ? "Digest preview computed" : "Digest sent", {
        description: `processed ${data.processed} · ${dryRun ? "would send" : "sent"} ${data.sent}${data.failed ? ` · failed ${data.failed}` : ""}`,
      });
    } catch (e) {
      toast("Digest failed", { description: e.message });
    } finally {
      setDigestLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await authFetch("/api/admin/stats");
        if (!cancelled) setStats(data);
      } catch {
        /* handled by authFetch (toast/redirect) */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const val = (k) => (loading ? "…" : stats?.[k] ?? 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              data-testid={`overview-card-${c.key}`}
              className={`relative overflow-hidden rounded-2xl border ${c.border} bg-[#112240] p-5`}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 border border-white/10 mb-3">
                <Icon size={16} className="text-[#00B4D8]" />
              </div>
              <div data-testid={`overview-value-${c.key}`} className="text-3xl font-black tracking-tight font-mono-gx">
                {val(c.key)}
              </div>
              <div className="text-xs text-slate-400 mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>

      <div className="rounded-3xl border border-[#4CAF50]/30 bg-gradient-to-r from-[#0f2b1a] via-[#112240] to-[#112240] p-6 lg:p-8">
        <h2 className="font-display font-black text-2xl tracking-tight mb-1">Welcome back, GeoNetix Admin</h2>
        <p className="text-sm text-slate-400 mb-5">
          Review pending KYC submissions and mapping approvals from the sidebar.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="quick-review-kycs"
            onClick={() => onNavigate("kyc")}
            className="inline-flex items-center gap-2 rounded-full border border-[#00B4D8]/50 bg-[#00B4D8]/10 px-5 py-2.5 text-sm font-semibold text-[#00B4D8] hover:brightness-110 transition"
          >
            <FileText size={15} /> Review KYCs <ArrowRight size={14} />
          </button>
          <button
            data-testid="quick-approve-mappings"
            onClick={() => onNavigate("mapping")}
            className="inline-flex items-center gap-2 rounded-full border border-[#4CAF50]/50 bg-[#4CAF50]/10 px-5 py-2.5 text-sm font-semibold text-[#4CAF50] hover:brightness-110 transition"
          >
            <Map size={15} /> Approve Mappings <ArrowRight size={14} />
          </button>
          <button
            data-testid="quick-assign-devices"
            onClick={() => onNavigate("users")}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:brightness-110 transition"
          >
            <KeyRound size={15} /> Assign Device Numbers <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-[#00B4D8]/25 bg-gradient-to-r from-[#0c2540] via-[#112240] to-[#112240] p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
          <div>
            <h2 className="font-display font-black text-xl tracking-tight">Weekly Earnings Digest</h2>
            <p className="text-sm text-slate-400 mt-1">
              Emails every active user a Sunday-style summary of km mapped, data shared and ₹ earned in the last 7 days. Uses Resend (needs RESEND_API_KEY set on prod).
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="digest-dry-run"
            onClick={() => sendDigest(true)}
            disabled={digestLoading}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:brightness-110 disabled:opacity-60 transition"
          >
            {digestLoading ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
            Preview (no send)
          </button>
          <button
            data-testid="digest-send"
            onClick={() => sendDigest(false)}
            disabled={digestLoading}
            className="inline-flex items-center gap-2 rounded-full bg-[#4CAF50] px-5 py-2.5 text-sm font-semibold text-[#04140a] hover:brightness-110 disabled:opacity-60 transition"
          >
            {digestLoading ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
            Send weekly digest now
          </button>
        </div>
      </div>
    </div>
  );
}
